/**
 * @file useOpenChatResolver.ts
 * @description Куда открыть окно по `openChat(...)` (chat-window-design.md
 * §6 «Адреса»):
 *  - `conversationId` — сразу этот диалог;
 *  - `customerId` + `orderId` — найти или создать диалог по заказу
 *    (`POST /admin/chat/conversations`, архивный возвращается сам);
 *  - только `customerId` — отбор «Клиент: … ×» и самый свежий диалог; нет
 *    диалогов — создаётся «Общий чат».
 *
 * Вход под сотрудником (`readOnly`) диалоги не создаёт (сервер ответил бы
 * 403): ищем существующий через список (действующие, затем архив) с отбором
 * по клиенту и заказу (`contextOrderId`). Не нашли — окно открывается на списке со спокойным
 * сообщением «Диалога с клиентом ещё нет…».
 *
 * Ошибка — всплывающее сообщение человеческим текстом; окно при этом
 * открывается на списке, чтобы человек не остался ни с чем.
 */

import { useCallback } from "react";

import { useNotify } from "../ui/NotifyProvider";
import { adminChat, type StaffConversation } from "../../lib/api-chat";
import { chatErrorText } from "./chat-errors";
import { customerName } from "./chat-format";
import { CHAT_TEXT } from "./chat-texts";
import type { OpenChatTarget } from "./chat-window-context";
import type { ChatCustomerFilter } from "./chat-window-state";

export interface ResolvedChatTarget {
  selectedId: string | null;
  customer?: ChatCustomerFilter | null;
  /** Спокойное сообщение над списком. */
  notice?: string;
}

/**
 * Найти существующий диалог клиента (и заказа, если задан) — без создания.
 * Диалог заказа сервер отбирает сам (`contextOrderId`), перебора нет.
 */
async function findExisting(customerId: string, orderId: string | undefined): Promise<StaffConversation | null> {
  for (const tab of ["all", "archive"] as const) {
    const page = await adminChat.list(orderId ? { tab, customerId, contextOrderId: orderId, limit: 1 } : { tab, customerId, limit: 1 });
    const found = page.items[0];
    if (found) return found;
  }
  return null;
}

export function useOpenChatResolver(readOnly: boolean) {
  const notify = useNotify();

  return useCallback(
    async (target: OpenChatTarget): Promise<ResolvedChatTarget> => {
      const filter = target.customerId && !target.orderId
        ? { id: target.customerId, name: target.customerName ?? "…" }
        : undefined;
      try {
        if (target.conversationId) return { selectedId: target.conversationId, customer: filter };
        if (readOnly && target.customerId) {
          const found = await findExisting(target.customerId, target.orderId);
          if (found) {
            return target.orderId
              ? { selectedId: found.id }
              : { selectedId: found.id, customer: { id: target.customerId, name: customerName(found.customer) } };
          }
          return {
            selectedId: null,
            customer: { id: target.customerId, name: target.customerName ?? "…" },
            notice: CHAT_TEXT.noConversationReadOnly,
          };
        }
        if (target.customerId && target.orderId) {
          const conv = await adminChat.ensure({ customerId: target.customerId, contextOrderId: target.orderId });
          return { selectedId: conv.id };
        }
        if (target.customerId) {
          const page = await adminChat.list({ tab: "all", customerId: target.customerId, limit: 1 });
          const first = page.items[0];
          if (first) return { selectedId: first.id, customer: { id: target.customerId, name: customerName(first.customer) } };
          const conv = await adminChat.ensure({ customerId: target.customerId });
          return { selectedId: conv.id, customer: { id: target.customerId, name: customerName(conv.customer) } };
        }
        return { selectedId: null };
      } catch (error) {
        notify.error(chatErrorText(error, "Не удалось открыть переписку с клиентом. Попробуйте ещё раз"));
        return { selectedId: null, customer: filter };
      }
    },
    [notify, readOnly],
  );
}
